import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { TrendingDown, Clock, Brain, Users, ArrowRight, Check, Calculator, Home, Briefcase, PieChart } from 'lucide-react';
import { Link } from 'react-router-dom';

const WealthBuilderPage = () => {
  const challenges = [
    {
      icon: TrendingDown,
      title: "Wealth Can Erode Without Structure",
      description: "Without a long-term plan, your wealth may not last as long as you expect.",
      points: [
        "Ad hoc investing often leads to fragmented portfolios and risk exposure",
        "Overdependence on one business or asset class can increase vulnerability",
        "Taxes, inflation, and lifestyle creep can quietly chip away your wealth"
      ]
    },
    {
      icon: Clock,
      title: "Time is Scarce for Business Owners",
      description: "Running a business leaves little room to manage complex investments.",
      points: [
        "Monitoring market trends, regulations, and taxes requires full-time attention",
        "DIY investing often lacks professional risk management and discipline",
        "Delegating to a trusted expert helps you focus on what you do best"
      ]
    },
    {
      icon: Brain,
      title: "The 'Next Big Thing' Trap",
      description: "Everyone wants the next multi-bagger, but that mindset can backfire.",
      points: [
        "Chasing trends often leads to losses and missed fundamentals",
        "True wealth compounds through patience, not speculation",
        "We help you build conviction-based portfolios aligned to your goals"
      ]
    },
    {
      icon: Users,
      title: "Multi-Generational Planning",
      description: "Legacy is more than inheritance—it's about preparing the next generation.",
      points: [
        "Proper estate planning ensures your assets don't fall into legal disputes",
        "We help educate and prepare heirs for wealth stewardship",
        "Your values and legacy deserve a long-term blueprint—not guesswork"
      ]
    }
  ];

  const calculators = [
    {
      icon: Calculator,
      title: "Crorepati Calculator",
      features: ["Target Amount: Aim for ₹1 Cr+ corpus", "Time Horizon: Set your goal year", "SIP Needed: Plan monthly investment"],
      link: "/calculators"
    },
    {
      icon: Briefcase,
      title: "Business Planning",
      features: ["Valuation Target: Know your growth goal", "Cash Flow: Plan stable income post-retirement", "Exit Strategy: Build with SIP & reserves"],
      link: "/calculators"
    },
    {
      icon: Home,
      title: "House Planning",
      features: ["SWP: Plan monthly payouts smartly", "Corpus Goal: Set your housing fund target", "Withdrawal Plan: Keep it tax-smart & stable"],
      link: "/calculators"
    },
    {
      icon: PieChart,
      title: "Portfolio Planner",
      features: ["Asset Allocation: Plan investments across classes", "Corpus: Set your retirement goal", "SWP: Estimate post-retirement withdrawals"],
      link: "/calculators"
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 mb-6">
                <TrendingDown className="w-4 h-4 text-emerald-400" />
                <span className="text-white/60 text-sm">Specialised Investment Services</span>
              </div>

              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Why Specialised Investment Services Matters for HNIs & Entrepreneurs
              </h1>

              <p className="text-lg text-white/60 leading-relaxed mb-8">
                Despite building impressive businesses and accumulating significant wealth, many affluent families and high-net-worth individuals struggle to preserve and grow that wealth across generations. The challenge isn't capability—it's often time, bandwidth, and structure.
              </p>

              <p className="text-white/80 mb-8">
                At <span className="text-emerald-400 font-semibold">Frontier Investments</span>, we help you move beyond scattered strategies and "next big thing" investing. Our integrated capital-building framework is designed to help you sustain, grow, and protect your wealth—without chasing volatility.
              </p>

              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black font-semibold rounded-full hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all duration-500 group"
              >
                Get Started
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800&h=600&fit=crop"
                alt="Wealth Building"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {challenges.map((challenge, index) => {
              const IconComponent = challenge.icon;
              return (
                <div
                  key={index}
                  className="p-8 bg-gray-50 rounded-3xl border border-gray-100 hover:shadow-xl transition-all duration-500"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-4 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-black">{challenge.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-6">{challenge.description}</p>
                  <ul className="space-y-3">
                    {challenge.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="p-1 bg-emerald-100 rounded-full mt-1">
                          <Check className="w-3 h-3 text-emerald-600" />
                        </div>
                        <span className="text-gray-600 text-sm">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Calculators Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Build Your Wealth With Our Calculators
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Use our suite of financial calculators to plan your wealth-building journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {calculators.map((calc, index) => {
              const IconComponent = calc.icon;
              return (
                <Link
                  key={index}
                  to={calc.link}
                  className="group p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-500"
                >
                  <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl w-fit mb-4">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-4">{calc.title}</h3>
                  <ul className="space-y-2">
                    {calc.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span className="text-white/60 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">
            Ready to build lasting wealth?
          </h2>
          <p className="text-gray-500 mb-10">
            Schedule a free consultation with our investment experts
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-black text-white font-semibold rounded-full hover:shadow-lg transition-all duration-500 group"
          >
            Book a Consultation
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WealthBuilderPage;
